// Ported from markup_parser/src/lib/parser.ts.
// Parses the cumulative flow grammar (one [id] statement per line) into a
// ParsedFlow with structured Statements + ParseErrors. Also exports
// splitFacilitatorResponse for splitting the markdown three-section reply
// into { flowText, openQuestions, nextQuestion } — kept in sync with the
// server-side splitter in functions/app.js.

import type {
  Actor,
  ActorKind,
  Guard,
  ParseError,
  ParsedFlow,
  Statement,
  StatementType,
  Trigger,
  TriggerExpr,
} from './flowModel';

class Tokenizer {
  private pos = 0;
  constructor(private readonly src: string) {}

  eof(): boolean {
    this.skipWs();
    return this.pos >= this.src.length;
  }

  skipWs(): void {
    while (this.pos < this.src.length && /\s/.test(this.src[this.pos])) {
      this.pos++;
    }
  }

  peek(): string {
    this.skipWs();
    return this.src[this.pos] ?? '';
  }

  rest(): string {
    return this.src.slice(this.pos);
  }

  consume(s: string): boolean {
    this.skipWs();
    if (this.src.startsWith(s, this.pos)) {
      this.pos += s.length;
      return true;
    }
    return false;
  }

  expect(s: string): void {
    if (!this.consume(s)) {
      throw new Error(`Expected "${s}" at "${this.rest().slice(0, 20)}"`);
    }
  }

  readId(): number {
    this.skipWs();
    if (this.src[this.pos] !== '[') throw new Error('Expected [');
    this.pos++;
    const start = this.pos;
    while (this.pos < this.src.length && /\d/.test(this.src[this.pos])) this.pos++;
    if (start === this.pos) throw new Error('Expected integer inside []');
    const n = parseInt(this.src.slice(start, this.pos), 10);
    this.skipWs();
    if (this.src[this.pos] !== ']') throw new Error('Expected ]');
    this.pos++;
    return n;
  }
}

function parseTriggerExpr(rest: string): TriggerExpr {
  const t = new Tokenizer(rest);
  const result = parseOr(t);
  if (!t.eof()) throw new Error('Trailing tokens in trigger expr');
  return result;

  function parseOr(t: Tokenizer): TriggerExpr {
    let left = parseAnd(t);
    while (t.peek() === '|') {
      t.consume('|');
      const right = parseAnd(t);
      left = { op: 'or', left, right };
    }
    return left;
  }

  function parseAnd(t: Tokenizer): TriggerExpr {
    let left = parseAtom(t);
    while (t.peek() === '&') {
      t.consume('&');
      const right = parseAtom(t);
      left = { op: 'and', left, right };
    }
    return left;
  }

  function parseAtom(t: Tokenizer): TriggerExpr {
    if (t.peek() === '(') {
      t.consume('(');
      const inner = parseOr(t);
      t.expect(')');
      return inner;
    }
    const id = t.readId();
    return { ref: id };
  }
}

function actorKindFromSigil(sigil: string): ActorKind {
  if (sigil === '@!') return 'human';
  if (sigil === '@^') return 'externalSystem';
  return 'context';
}

function parseStatement(line: string, errors: ParseError[]): Statement | null {
  const source = line;
  const trimmed = line.trim();
  if (!trimmed || trimmed.startsWith('#')) return null;

  const m = trimmed.match(/^\[(\d+)\]\s*(.*)$/s);
  if (!m) {
    errors.push({
      statementId: null,
      severity: 'error',
      code: 'parse_error',
      message: `Line does not start with [id]`,
      source,
    });
    return null;
  }
  const id = parseInt(m[1], 10);
  let rest = m[2];

  const tags: Record<string, string | true> = {};
  const tagRe = /(?:^|\s)(#[A-Za-z_][A-Za-z0-9_]*(?:=[A-Za-z0-9_]+)?)\s*$/;
  while (true) {
    const tm = rest.match(tagRe);
    if (!tm) break;
    const tagText = tm[1].slice(1);
    const [k, v] = tagText.split('=');
    tags[k] = v ?? true;
    rest = rest.slice(0, rest.length - tm[0].length).trimEnd();
  }

  // Guard clause: `? Condition` or `? !Condition`, appearing after the
  // trigger clause and before tags. Strip it before the trigger is parsed so
  // the trailing condition identifier doesn't leak into the trigger expr.
  let guard: Guard | null = null;
  const guardMatch = rest.match(/\?\s*(!?)\s*([A-Za-z_][A-Za-z0-9_]*)\s*$/);
  if (guardMatch) {
    guard = { condition: guardMatch[2], negated: guardMatch[1] === '!' };
    rest = rest.slice(0, rest.length - guardMatch[0].length).trimEnd();
  }

  let triggeredBy: Trigger | null = null;
  const fromMatch = rest.match(/::\s*from\s*\[(\d+)\]\s*$/);
  const onMatchStart = rest.search(/::\s*on\s+/);
  if (fromMatch) {
    triggeredBy = { kind: 'from', actionId: parseInt(fromMatch[1], 10) };
    rest = rest.slice(0, rest.length - fromMatch[0].length).trimEnd();
  } else if (onMatchStart >= 0) {
    const exprText = rest.slice(onMatchStart).replace(/^::\s*on\s+/, '').trim();
    try {
      triggeredBy = { kind: 'on', expr: parseTriggerExpr(exprText) };
    } catch (e) {
      errors.push({
        statementId: id,
        severity: 'error',
        code: 'parse_error',
        message: `Could not parse trigger expression: ${(e as Error).message}`,
        source,
      });
    }
    rest = rest.slice(0, onMatchStart).trimEnd();
  }

  let emits: number[] = [];
  const emitMatch = rest.match(/=>\s*\{([^}]*)\}\s*$/);
  if (emitMatch) {
    const inside = emitMatch[1].trim();
    if (inside.length) {
      const ids = inside.match(/\[(\d+)\]/g) ?? [];
      emits = ids.map((s) => parseInt(s.slice(1, -1), 10));
    }
    rest = rest.slice(0, rest.length - emitMatch[0].length).trimEnd();
  }

  let via: { sys: string[] } | null = null;
  // %sys names are permissive — allow dots and hyphens so real-world hostnames
  // (ryanair.com, api-gateway.stripe.com) pass through instead of silently
  // dropping the via clause. One [via ...] block may list multiple %sys
  // touchpoints — "[via %sys Website %sys MobileApp]" — when the same action
  // happens through either one.
  const viaMatch = rest.match(/\[via\s+(%sys\s+[A-Za-z0-9_.-]+(?:\s+%sys\s+[A-Za-z0-9_.-]+)*)\]\s*$/);
  if (viaMatch) {
    const sysNames = [...viaMatch[1].matchAll(/%sys\s+([A-Za-z0-9_.-]+)/g)].map((m) => m[1]);
    via = { sys: sysNames };
    rest = rest.slice(0, rest.length - viaMatch[0].length).trimEnd();
  }

  let actor: Actor | null = null;
  let type: StatementType;
  let name = '';
  let target: string | null = null;

  const headerParts = rest.split(/\s+>\s+/);

  const typeIdx = headerParts.findIndex((p) =>
    /^\((action|query|command|event|reaction)\)/.test(p.trim()),
  );
  if (typeIdx === -1) {
    errors.push({
      statementId: id,
      severity: 'error',
      code: 'parse_error',
      message: `No (type) token found`,
      source,
    });
    return null;
  }

  if (typeIdx > 0) {
    const actorText = headerParts[0].trim();
    // Actor names use a permissive validator: after the `@` and optional
    // sigil (`!` for human, `^` for external system), accept any non-empty
    // body that contains at least one letter. This handles hostname-style
    // initiators (@^stripe.com), natural BC names with spaces
    // (@Customer Identity), and BC names with separators like slashes,
    // commas, parentheses, and en-dashes (@Onboarding / Start of Supply,
    // @Collections – PPP Recoup, @Electricity Metering – ROI (L1e / L100 / NSMP)).
    // The `>` separator already split the actor from the rest so we don't
    // need to defend against it here.
    if (!actorText.startsWith('@')) {
      errors.push({
        statementId: id,
        severity: 'error',
        code: 'parse_error',
        message: `Invalid actor: ${actorText}`,
        source,
      });
    } else {
      let body = actorText.slice(1);
      let sigilChar = '';
      if (body.startsWith('!') || body.startsWith('^')) {
        sigilChar = body[0];
        body = body.slice(1);
      }
      body = body.trim();
      if (!body || !/[A-Za-z]/.test(body)) {
        errors.push({
          statementId: id,
          severity: 'error',
          code: 'parse_error',
          message: `Invalid actor: ${actorText}`,
          source,
        });
      } else {
        actor = { name: body, kind: actorKindFromSigil('@' + sigilChar) };
      }
    }
  }

  const typePart = headerParts[typeIdx].trim();
  const typeMatch = typePart.match(/^\((action|query|command|event|reaction)\)\s*(.*)$/);
  if (!typeMatch) {
    errors.push({
      statementId: id,
      severity: 'error',
      code: 'parse_error',
      message: `Invalid behaviour token: ${typePart}`,
      source,
    });
    return null;
  }
  type = typeMatch[1] as StatementType;
  name = typeMatch[2].trim();

  if (typeIdx + 1 < headerParts.length) {
    const targetText = headerParts[typeIdx + 1].trim();
    const tm = targetText.match(/^&([A-Za-z_][A-Za-z0-9_]*)/);
    if (tm) target = tm[1];
  }

  if (!target) {
    errors.push({
      statementId: id,
      severity: 'error',
      code: 'missing_target',
      message: `Statement missing target`,
      source,
    });
  }

  return {
    id,
    type,
    actor,
    name,
    target,
    via,
    emits,
    triggeredBy,
    guard,
    tags,
    source,
  };
}

export function parseFlow(text: string): ParsedFlow {
  const errors: ParseError[] = [];
  const statements: Statement[] = [];
  const lines = text.split(/\r?\n/);

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) continue;
    if (line.trim().startsWith('```')) continue;
    if (!/^\s*\[\d+\]/.test(line)) continue;
    try {
      const st = parseStatement(line, errors);
      if (st) statements.push(st);
    } catch (e) {
      errors.push({
        statementId: null,
        severity: 'error',
        code: 'parse_error',
        message: (e as Error).message,
        source: line,
      });
    }
  }

  const byId = new Map<number, Statement>();
  for (const st of statements) {
    if (byId.has(st.id)) {
      errors.push({
        statementId: st.id,
        severity: 'error',
        code: 'duplicate_id',
        message: `Duplicate ID [${st.id}]`,
        source: st.source,
      });
    } else {
      byId.set(st.id, st);
    }
  }

  // Synthesize producer.emits from the natural form `[N] (event) X :: from [P]`.
  // Treating "event X was produced by P" as equivalent to writing
  // `[P] ... => { [N] }` on the producer. Without this, downstream layout
  // can't attach the event handle to P's card and reactions that listen
  // via `:: on [N]` dangle without visible edges.
  for (const ev of statements) {
    if (ev.type !== 'event') continue;
    if (ev.triggeredBy?.kind !== 'from') continue;
    const producer = byId.get(ev.triggeredBy.actionId);
    if (!producer) continue;
    if (!producer.emits.includes(ev.id)) producer.emits.push(ev.id);
  }

  // Resolve event actors from their producer (events have no actor sigil)
  for (const producer of statements) {
    for (const evId of producer.emits) {
      const ev = byId.get(evId);
      if (ev && ev.type === 'event' && !ev.actor && producer.actor) {
        ev.actor = producer.actor;
      }
    }
  }

  const actorKey = (a: Actor) => `${a.kind}:${a.name}`;
  const actorsMap = new Map<string, Actor>();
  const systemsMap = new Map<string, { name: string }>();
  const targetsMap = new Map<string, { name: string }>();

  for (const st of statements) {
    if (st.actor) {
      const k = actorKey(st.actor);
      if (!actorsMap.has(k)) actorsMap.set(k, st.actor);
    }
    if (st.via) {
      for (const sys of st.via.sys) {
        if (!systemsMap.has(sys)) systemsMap.set(sys, { name: sys });
      }
    }
    if (st.target && !targetsMap.has(st.target)) {
      targetsMap.set(st.target, { name: st.target });
    }
  }

  return {
    statements,
    actors: [...actorsMap.values()],
    systems: [...systemsMap.values()],
    targets: [...targetsMap.values()],
    errors,
  };
}

export function extractFlowBlock(raw: string): string {
  const lines = raw.split(/\r?\n/);
  const out: string[] = [];
  let inFlow = false;
  let inCode = false;
  for (const line of lines) {
    const trimmed = line.trim();
    if (/^#{1,6}\s*flow\b/i.test(trimmed) || /^\*\*flow\*\*/i.test(trimmed)) {
      inFlow = true;
      continue;
    }
    if (inFlow && /^#{1,6}\s*(open\s*questions|next\s*question)\b/i.test(trimmed)) break;
    if (inFlow && /^\*\*(open\s*questions|next\s*question)\*\*/i.test(trimmed)) break;
    if (inFlow) {
      if (trimmed.startsWith('```')) {
        inCode = !inCode;
        continue;
      }
      out.push(line);
    }
  }
  if (!inFlow) {
    for (const line of lines) {
      if (/^\s*\[\d+\]/.test(line)) out.push(line);
    }
  }
  return out.join('\n');
}

export function splitFacilitatorResponse(raw: string) {
  const flowText = extractFlowBlock(raw);
  const extractSection = (label: RegExp, stop: RegExp): string => {
    const lines = raw.split(/\r?\n/);
    const out: string[] = [];
    let inSection = false;
    for (const line of lines) {
      const t = line.trim();
      if (label.test(t)) {
        inSection = true;
        continue;
      }
      if (inSection && stop.test(t)) break;
      if (inSection) out.push(line);
    }
    return out.join('\n').trim();
  };

  const openQuestions = extractSection(
    /^(#{1,6}\s*open\s*questions|^\*\*open\s*questions\*\*)/i,
    /^(#{1,6}\s*next\s*question|^\*\*next\s*question\*\*)/i,
  );
  const nextQuestion = extractSection(
    /^(#{1,6}\s*next\s*question|^\*\*next\s*question\*\*)/i,
    /^(#{1,6}|^---\s*$)/i,
  );

  return { flowText: flowText.trim(), openQuestions, nextQuestion, raw };
}
